import { useState } from "react"
import { useFormContext } from "react-hook-form"
import * as Yup from "yup"

import useIsDesktop from "@hooks/useIsDesktop"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms"

import { Dialog } from "@molecules"
import { EmailInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import { MutationSuccessFunctionParams } from "@molecules/Form/GenericForm/GenericForm.types"
import { InputHelpTextProps } from "@molecules/Form/inputs/Input/InputHelpText/InputHelpText.types"
import toast from "@molecules/Toast/Toast"

import { SecurityCheckDialog } from "@organisms/index"

import { USER_ENDPOINT } from "@endpoints/user"

import styles from "./EmailFlowDialog.module.scss"
import {
    EmailChangeErrorResponse,
    EmailFlowDialogProps,
    EmailFlowInputProps,
    EmailFlowSubmitButtonProps,
    UserEmail,
} from "./EmailFlowDialog.types"

const UNCHANGED_EMAIL_HELP_TEXT: InputHelpTextProps = {
    message: "This is your current email address.",
    status: "info",
}

const formSchema: Yup.ObjectSchema<UserEmail> = Yup.object().shape({
    email: Yup.string().required("Please enter an email address").email("Please enter a valid email address"),
})

function EmailChangeInput(props: EmailFlowInputProps) {
    const { userEmail } = props
    const { watch } = useFormContext<UserEmail>()
    const currentEmail = watch("email")

    const isUnchanged = !!userEmail && currentEmail === userEmail

    return (
        <EmailInput<UserEmail>
            label="New email address"
            size="md"
            placeholder="Enter new email address..."
            aria-label="New email address"
            name="email"
            autoFocus={true}
            inputHelpText={isUnchanged ? UNCHANGED_EMAIL_HELP_TEXT : undefined}
        />
    )
}

function SubmitButton(props: EmailFlowSubmitButtonProps) {
    const { userEmail, isEmailVerified, isPending } = props
    const { watch } = useFormContext<UserEmail>()
    const currentEmail = watch("email")

    const isUnchangedAndVerified = currentEmail === userEmail && isEmailVerified

    return (
        <Button variant="solid" size="lg" type="submit" isProcessing={isPending} noDebounce={true}>
            {isUnchangedAndVerified ? "Save" : "Send code"}
        </Button>
    )
}

export default function EmailFlowDialog(props: EmailFlowDialogProps) {
    const { children, defaultOpen = false } = props

    const { user, updateUser } = useUser()
    const isDesktop = useIsDesktop()

    const [isChangeEmailDialogOpen, setIsChangeEmailDialogOpen] = useState<boolean>(defaultOpen)
    const [isVerifyingEmail, setIsVerifyingEmail] = useState<boolean>(false)
    const [isSecurityCheckOpen, setIsSecurityCheckOpen] = useState<boolean>(false)

    const handleTriggerClick = () => {
        if (!user?.email_verified) {
            setIsSecurityCheckOpen(true)
        } else {
            setIsChangeEmailDialogOpen(true)
        }
    }

    const handleVerificationSuccess = () => {
        updateUser({ email_verified: true })

        setIsSecurityCheckOpen(false)
        setIsVerifyingEmail(false)

        toast({
            variant: "default",
            title: "Email address verified",
            type: "success",
        })
    }

    const handleChangeEmailSuccess = (args: MutationSuccessFunctionParams<UserEmail, User>) => {
        const emailWasChanged = user?.email !== args.formValues.email

        setIsChangeEmailDialogOpen(false)

        if (emailWasChanged) {
            setIsVerifyingEmail(true)
            setIsSecurityCheckOpen(true)

            updateUser({
                email: args.formValues.email,
                email_verified: false,
            })
        } else if (!user?.email_verified) {
            // Unchanged but unverified - open verification without setting isVerifyingEmail
            // so it uses "verify" entry point instead of "change"
            setIsSecurityCheckOpen(true)
        }
    }

    const handleSecurityCheckLeftAction = () => {
        setIsSecurityCheckOpen(false)
        setIsChangeEmailDialogOpen(true)
    }

    const handleSecurityCheckDiscard = () => {
        setIsSecurityCheckOpen(false)
        setIsVerifyingEmail(false)
    }

    return (
        <>
            {children({ onClick: handleTriggerClick })}

            <Dialog.Root
                isOpen={isChangeEmailDialogOpen}
                onOpenChange={setIsChangeEmailDialogOpen}
                mode="dialog"
                variant="default"
            >
                <Dialog.Content>
                    <GenericForm<UserEmail, User, EmailChangeErrorResponse, UserEmail>
                        formConfig={{
                            defaultValues: {
                                email: "",
                            },
                        }}
                        formValidationSchema={formSchema}
                        mutationConfig={{
                            genericErrorMessage: "Could not update email address",
                            method: "PATCH",
                            endpoint: USER_ENDPOINT,
                        }}
                        onMutationSuccess={handleChangeEmailSuccess}
                    >
                        {({ isPending }) => (
                            <>
                                <Dialog.Header.Root>
                                    <Dialog.Header.Title title="Change email address" />
                                    {isDesktop && <Dialog.Header.CloseButton />}
                                </Dialog.Header.Root>
                                <Dialog.Body className={styles.dialogBody}>
                                    <span className={styles.dialogBodyText}>
                                        Enter a new email address. We&apos;ll send a confirmation code to verify it.
                                    </span>
                                    <EmailChangeInput userEmail={user?.email} />
                                </Dialog.Body>
                                <Dialog.Footer.Root>
                                    <Button
                                        variant="ghost"
                                        size="lg"
                                        onClick={() => setIsChangeEmailDialogOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <SubmitButton
                                        userEmail={user?.email}
                                        isEmailVerified={user?.email_verified}
                                        isPending={isPending}
                                    />
                                </Dialog.Footer.Root>
                            </>
                        )}
                    </GenericForm>
                </Dialog.Content>
            </Dialog.Root>

            <SecurityCheckDialog
                checkFor="email"
                onSuccess={handleVerificationSuccess}
                onOpenChange={setIsSecurityCheckOpen}
                isOpen={isSecurityCheckOpen}
                onDiscard={handleSecurityCheckDiscard}
                title="Verify email address"
                entryPoint={isVerifyingEmail ? "change" : "verify"}
                leftActionOverride={
                    <Button
                        size="lg"
                        variant="ghost"
                        leftIcon={isVerifyingEmail ? faIcon({ name: "arrow-left", style: "regular" }) : undefined}
                        onClick={handleSecurityCheckLeftAction}
                    >
                        {isVerifyingEmail ? "Back" : "Correct email"}
                    </Button>
                }
            />
        </>
    )
}
